import type { DocPageContent } from '../docs-content'

const page = {
  order: 5,
  slug: 'media',
  title: 'Biblioteca de medios',
  description:
    'Importa medios, inspecciona archivos y genera proxies, transcripciones, subtítulos y datos de escena mediante IA.',
  category: 'Edición principal',
  related: ['source-monitor', 'timeline', 'scene-browser', 'export'],
  sections: [
    {
      title: 'Importar medios',
      blocks: [
        {
          kind: 'list',
          items: [
            'Abre la pestaña **Medios** y usa **Importar** para elegir archivos, o arrastra los archivos directamente a la biblioteca.',
            'CachoCut admite video, audio, imágenes, GIFs, SVGs, animaciones Lottie (`.json` y `.lottie`) y recursos generados. Los GIFs se importan como elementos de imagen.',
            'Usa **Importar medios desde URL** para un enlace directo a un archivo de medios; la URL debe apuntar al archivo en sí, no a una página que lo incruste.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'El metraje Apple ProRes se importa, se previsualiza y genera miniaturas como cualquier otro clip, incluidos formatos que no pueden decodificar por sí solos. Al exportar, los clips ProRes se recodifican al códec de salida elegido; no existe un formato de salida ProRes.',
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'La importación de carpetas aún no es compatible: suelta los archivos de medios directamente. Si un archivo no se importa, prueba con otro formato o transcódalo primero.',
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'El metraje en formato log (S-Log3 y similares) se ve plano y desaturado por diseño; así es como se captura. Agrega un LUT o una corrección de color para recuperarlo; no hay nada malo con el archivo.',
        },
      ],
    },
    {
      title: 'Inspeccionar un clip',
      blocks: [
        {
          kind: 'list',
          items: [
            'Abre **Información de medios** para ver el códec, las dimensiones, la duración, la velocidad de fotogramas, el tamaño del archivo, el tipo y el estado de la transcripción.',
            'Haz doble clic en una tarjeta de medio, o usa **Abrir en el monitor de origen**, para previsualizar una fuente antes de editarla.',
            'Las tarjetas de medios exponen menús para Archivo, Proxy, Transcripción, Subtítulos incrustados y acciones de IA.',
            'Ordena, filtra y agrupa recursos por tipo cuando la biblioteca crezca.',
          ],
        },
      ],
    },
    {
      title: 'Generar datos de soporte',
      blocks: [
        {
          kind: 'table',
          headers: ['Acción', 'Qué obtienes'],
          rows: [
            [
              'Generar proxy',
              'Una versión más liviana de un video pesado para editar con mayor fluidez; la exportación sigue usando el original.',
            ],
            ['Generar transcripción', 'Texto de voz editable que puedes buscar y convertir en subtítulos.'],
            [
              'Extraer subtítulos incrustados',
              'Pistas de subtítulos extraídas del archivo, listas para insertar en la línea de tiempo.',
            ],
            [
              'Analizar con IA',
              'Detección de escenas y generación de descripciones local, para que los clips sean buscables en el Explorador de escenas.',
            ],
          ],
        },
      ],
    },
    {
      title: 'Clips compuestos',
      blocks: [
        {
          kind: 'list',
          items: [
            'Agrupa una sección de la línea de tiempo en un **clip compuesto** que se comporta como un único elemento de medio reutilizable.',
            'Los clips compuestos aparecen en la biblioteca de medios y se pueden colocar nuevamente como cualquier otro recurso.',
            'Haz clic derecho en un clip compuesto y elige **Abrir como pestaña** para editarlo como una **secuencia** independiente.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Los clips compuestos se pueden anidar (uno puede contener a otro), siempre que la cadena nunca vuelva a referirse a sí misma.',
        },
      ],
    },
    {
      title: 'Animaciones Lottie',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Importa archivos Lottie (archivos `.json` sin procesar o archivos empaquetados `.lottie`) como elementos de la línea de tiempo que se renderizan con precisión de fotograma en la vista previa y en la exportación. Selecciona un clip Lottie para ver sus controles de edición en la sección **Lottie** del panel de propiedades.',
        },
        {
          kind: 'list',
          items: [
            'Controla la **Velocidad**, **Inversión**, **Bucle** y la reproducción **Ping-pong**.',
            'Define un segmento de **Fotograma inicial** / **Fotograma final** para reproducir solo parte de la animación, o elige un **Marcador** con nombre para llevar el segmento hasta él.',
            'Recolorea la animación: los **Colores** con nombre de autor aparecen primero, y el resto de las formas queda oculto bajo un desplegable; un color compartido edita cada forma que lo usó, y **Restablecer todo** recupera los originales.',
            'Edita las capas de **Texto** de la plantilla en el lugar, y ajusta las ranuras de valores escalares y vectoriales expuestas en **Propiedades**.',
            'En los archivos `.lottie` que incluyen más de una animación, cambia entre ellas con el selector de **Animación**; intercambia paletas con el selector de **Tema** cuando el archivo incluya temas.',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Las ediciones de color, texto, ranura y segmento se previsualizan en vivo en el lienzo mientras arrastras o escribes, y se confirman como un único paso de deshacer. La edición de animación, tema, marcador, segmento y plantilla se aplica a un solo clip Lottie seleccionado a la vez.',
        },
      ],
    },
    {
      title: 'Solucionar medios faltantes',
      blocks: [
        {
          kind: 'note',
          tone: 'warning',
          text: 'Medios faltantes aparece cuando un archivo vinculado se movió, se renombró, se eliminó o necesita permiso renovado. Usa **Conceder acceso**, **Localizar**, **Localizar carpeta** o **Explorar otra carpeta** para restaurar el enlace. Usa **Trabajar sin conexión** solo si tienes previsto volver a vincularlo más adelante.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
